import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ClipboardList,
  User,
  Phone,
  MapPin,
  Home,
  Building2,
  Landmark,
  DollarSign,
  Send,
  CheckCircle,
  Loader2,
  Layers,
  Share2,
  Eye,
  X
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';
import requestsService from '../services/requestsService';
import locationService from '../services/locationService';
import { buildOfferShareUrl } from '../utils/offerShareUtils';
import './PublicRequestForm.css';

const PublicRequestForm = () => {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ar';
  const [searchParams] = useSearchParams();
  const referralUserId = searchParams.get('ref');
  const [submitted, setSubmitted] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [previewOffer, setPreviewOffer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [cities, setCities] = useState([]);
  const [areasByCity, setAreasByCity] = useState({});
  const [districtsByArea, setDistrictsByArea] = useState({});
  const [allDistrictsByCity, setAllDistrictsByCity] = useState({});
  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    propertyType: 'villa',
    requestType: 'buy',
    city: 'الجبيل',
    region: '',
    districts: [],
    budget: '',
    paymentMethod: 'cash',
    paymentPlan: 'single',
    age: '',
    floor: 'any',
    clientSeriousness: 'interested',
    requirements: '',
    preferredContact: 'whatsapp'
  });

  const propertyTypes = [
    { value: 'villa', labelKey: 'offers.villa', icon: Home },
    { value: 'apartment', labelKey: 'offers.apartment', icon: Building2 },
    { value: 'land', labelKey: 'offers.land', icon: Landmark },
    { value: 'duplex', labelKey: 'offers.duplex', icon: Building2 },
    { value: 'commercial', labelKey: 'offers.commercial', icon: Building2 }
  ];

  // Fetch cities from API
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoadingLocations(true);
        const response = await locationService.getAllCities(currentLang);
        if (response.success && response.data.cities) {
          const citiesData = response.data.cities;
          setCities(citiesData);
          // Build areas and districts maps
          const areasMap = {};
          const districtsMap = {};
          const allDistrictsMap = {};
          citiesData.forEach(city => {
            areasMap[city.label] = (city.areas || []).map(a => a.label);
            const cityAllDistricts = [];
            (city.areas || []).forEach(area => {
              const areaDistricts = (area.districts || []).map(d => d.label);
              districtsMap[area.label] = areaDistricts;
              cityAllDistricts.push(...areaDistricts);
            });
            (city.unassignedDistricts || []).forEach(d => cityAllDistricts.push(d.label));
            allDistrictsMap[city.label] = cityAllDistricts;
          });
          setAreasByCity(areasMap);
          setDistrictsByArea(districtsMap);
          setAllDistrictsByCity(allDistrictsMap);
        }
      } catch (err) {
        console.error('Error fetching cities:', err);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchCities();
  }, [currentLang]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'city') {
      setFormData(prev => ({ ...prev, [name]: value, region: '', districts: [] }));
    } else if (name === 'region') {
      setFormData(prev => ({ ...prev, [name]: value, districts: [] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDistrictToggle = (district) => {
    setFormData(prev => ({
      ...prev,
      districts: prev.districts.includes(district)
        ? prev.districts.filter(d => d !== district)
        : [...prev.districts, district]
    }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.clientName || !formData.clientPhone || !formData.city) {
      toast.warning('الرجاء إدخال الاسم ورقم الهاتف والمدينة');
      return;
    }

    if (!formData.budget) {
      toast.warning('الرجاء إدخال الميزانية');
      return;
    }

    setLoading(true);
    
    try {
      const result = await requestsService.submitPublicRequest({
        ...formData,
        district: formData.districts.length > 0 ? formData.districts[0] : '',
        referralUserId
      });
      const requestId = result?.data?.request?._id;
      setSubmittedRequestId(requestId);
      setSubmitted(true);
      toast.success('تم إرسال طلبك بنجاح!');

      // Fetch matching offers for this request
      if (requestId) {
        setMatchesLoading(true);
        try {
          const matchResult = await requestsService.getPublicRequestMatches(requestId);
          setMatches(matchResult?.data?.matches || []);
        } catch (e) {
          console.error('Error fetching matches:', e);
        } finally {
          setMatchesLoading(false);
        }
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(error.message || 'حدث خطأ في إرسال الطلب');
    } finally {
      setLoading(false);
    }
  }, [formData, toast, referralUserId]);

  const resetForm = () => {
    setSubmitted(false);
    setSubmittedRequestId(null);
    setMatches([]);
    setPreviewOffer(null);
    setFormData({
      clientName: '',
      clientPhone: '',
      propertyType: 'villa',
      requestType: 'buy',
      city: 'الجبيل',
      region: '',
      districts: [],
      budget: '',
      paymentMethod: 'cash',
      paymentPlan: 'single',
      age: '',
      floor: 'any',
      clientSeriousness: 'interested',
      requirements: '',
      preferredContact: 'whatsapp'
    });
  };

  const getOfferPublicLink = (offer) => {
    return buildOfferShareUrl({
      username: offer?.username || offer?.marketer?.username || offer?.user?.username,
      catalogSlug: offer?.catalogSlug || offer?.catalog?.slug,
      offerId: offer?._id || offer?.id
    });
  };

  const handleShareOffer = (offer) => {
    const link = getOfferPublicLink(offer);
    if (navigator.share) {
      navigator.share({ title: offer.title || 'عرض عقاري', url: link }).catch(() => {});
    } else {
      navigator.clipboard.writeText(link).then(() => toast.success('تم نسخ رابط العرض'));
    }
  };

  if (submitted) {
    return (
      <div className="public-request-page">
        <div className="success-container">
          <div className="success-icon">
            <CheckCircle size={64} style={{ color: '#22c55e' }} />
          </div>
          <h1>تم إرسال طلبك بنجاح!</h1>
          <p>شكراً لك، سيتم التواصل معك قريباً من قبل أحد المسوقين العقاريين</p>

          {/* Matching offers section */}
          <div style={{ width: '100%', marginTop: '32px', textAlign: 'right' }}>
            {matchesLoading ? (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <Loader2 size={32} className="spinning" />
                <p style={{ marginTop: '8px', color: '#888' }}>جاري البحث عن العروض المطابقة...</p>
              </div>
            ) : matches.length > 0 ? (
              <>
                <h2 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '700' }}>
                  العروض المطابقة لطلبك ({matches.length})
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {matches.map(({ offer, score }) => (
                    <div key={offer._id} style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                    }}>
                      {/* Cover image */}
                      {offer.coverImage && (
                        <img
                          src={typeof offer.coverImage === 'string' ? offer.coverImage : offer.coverImage?.url}
                          alt={offer.title}
                          style={{ width: '72px', height: '72px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
                        />
                      )}
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>
                          {offer.title || 'عرض عقاري'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          {offer.city}{offer.district ? ` - ${offer.district}` : ''}
                        </div>
                        <div style={{ fontSize: '13px', color: '#444', marginTop: '2px' }}>
                          {offer.price ? `${Number(offer.price).toLocaleString('ar-SA')} ر.س` : ''}
                          {offer.area ? ` | ${offer.area} م²` : ''}
                        </div>
                      </div>
                      {/* Score badge */}
                      <div style={{
                        background: score >= 80 ? '#dcfce7' : score >= 60 ? '#fef9c3' : '#fee2e2',
                        color: score >= 80 ? '#166534' : score >= 60 ? '#854d0e' : '#991b1b',
                        borderRadius: '8px',
                        padding: '4px 10px',
                        fontWeight: '700',
                        fontSize: '13px',
                        flexShrink: 0
                      }}>
                        {score}%
                      </div>
                      {/* Action buttons */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                        <button
                          onClick={() => handleShareOffer(offer)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: '#3b82f6', color: '#fff',
                            border: 'none', borderRadius: '8px',
                            padding: '6px 12px', cursor: 'pointer', fontSize: '13px'
                          }}
                        >
                          <Share2 size={14} />
                          مشاركة العرض
                        </button>
                        <button
                          onClick={() => setPreviewOffer(offer)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: '#f3f4f6', color: '#374151',
                            border: '1px solid #d1d5db', borderRadius: '8px',
                            padding: '6px 12px', cursor: 'pointer', fontSize: '13px'
                          }}
                        >
                          <Eye size={14} />
                          معاينة العرض
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ textAlign: 'center', color: '#888', padding: '16px' }}>
                لا توجد عروض مطابقة لطلبك حالياً، سيتم التواصل معك عند توفر عروض مناسبة
              </p>
            )}
          </div>

          <button
            className="btn btn-primary"
            style={{ marginTop: '24px' }}
            onClick={resetForm}
          >
            إرسال طلب جديد
          </button>
        </div>

        {/* Offer preview modal */}
        {previewOffer && (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
              zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '16px'
            }}
            onClick={() => setPreviewOffer(null)}
          >
            <div
              style={{
                background: '#fff', borderRadius: '16px', maxWidth: '480px',
                width: '100%', maxHeight: '90vh', overflowY: 'auto',
                padding: '24px', position: 'relative', direction: 'rtl'
              }}
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setPreviewOffer(null)}
                style={{
                  position: 'absolute', top: '12px', left: '12px',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#666'
                }}
              >
                <X size={22} />
              </button>

              <h2 style={{ marginBottom: '16px', fontSize: '17px', fontWeight: '700', paddingLeft: '28px' }}>
                {previewOffer.title || 'عرض عقاري'}
              </h2>

              {/* Images */}
              {previewOffer.coverImage && (
                <img
                  src={typeof previewOffer.coverImage === 'string' ? previewOffer.coverImage : previewOffer.coverImage?.url}
                  alt={previewOffer.title}
                  style={{ width: '100%', borderRadius: '10px', objectFit: 'cover', maxHeight: '220px', marginBottom: '16px' }}
                />
              )}

              {/* Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                {previewOffer.city && (
                  <div><strong>المدينة:</strong> {previewOffer.city}{previewOffer.district ? ` - ${previewOffer.district}` : ''}</div>
                )}
                {previewOffer.price && (
                  <div><strong>السعر:</strong> {Number(previewOffer.price).toLocaleString('ar-SA')} ر.س</div>
                )}
                {previewOffer.area && (
                  <div><strong>المساحة:</strong> {previewOffer.area} م²</div>
                )}
                {previewOffer.rooms && (
                  <div><strong>الغرف:</strong> {previewOffer.rooms}</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
                <button
                  onClick={() => handleShareOffer(previewOffer)}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    background: '#3b82f6', color: '#fff', border: 'none',
                    borderRadius: '8px', padding: '10px', cursor: 'pointer', fontSize: '14px'
                  }}
                >
                  <Share2 size={16} />
                  مشاركة العرض
                </button>
                <a
                  href={getOfferPublicLink(previewOffer)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db',
                    borderRadius: '8px', padding: '10px', cursor: 'pointer',
                    fontSize: '14px', textDecoration: 'none'
                  }}
                >
                  <Eye size={16} />
                  فتح العرض
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="public-request-page">
      <div className="form-container">
        <div className="form-header">
          <div className="logo">
            <ClipboardList size={40} />
          </div>
          <h1>نموذج طلب عقار</h1>
          <p>أخبرنا عن العقار الذي تبحث عنه وسنساعدك في إيجاده</p>
        </div>

        <form onSubmit={handleSubmit} className="request-form">
          {/* Personal Info Section */}
          <div className="form-section">
            <h3>
              <User size={20} />
              المعلومات الشخصية
            </h3>
            
            <div className="form-row two-cols">
              <div className="form-group">
                <label>الاسم الكامل <span className="required">*</span></label>
                <div className="input-with-icon">
                  <User size={18} />
                  <input 
                    type="text"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleChange}
                    placeholder="الاسم الكامل"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>رقم الهاتف <span className="required">*</span></label>
                <div className="input-with-icon">
                  <Phone size={18} />
                  <input 
                    type="tel"
                    name="clientPhone"
                    value={formData.clientPhone}
                    onChange={handleChange}
                    placeholder="رقم الهاتف"
                    dir="ltr"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Request Type Section */}
          <div className="form-section">
            <h3>
              <ClipboardList size={20} />
              نوع الطلب
            </h3>
            
            <div className="property-type-selector">
              <label className={`type-option ${formData.requestType === 'buy' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="requestType"
                  value="buy"
                  checked={formData.requestType === 'buy'}
                  onChange={handleChange}
                />
                <DollarSign size={24} />
                <span>شراء</span>
              </label>
              <label className={`type-option ${formData.requestType === 'rent' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="requestType"
                  value="rent"
                  checked={formData.requestType === 'rent'}
                  onChange={handleChange}
                />
                <Home size={24} />
                <span>إيجار</span>
              </label>
            </div>
          </div>

          {/* Property Type Section */}
          <div className="form-section">
            <h3>
              <Home size={20} />
              نوع العقار المطلوب
            </h3>
            
            <div className="property-type-selector">
              {propertyTypes.map(type => {
                const Icon = type.icon;
                return (
                  <label 
                    key={type.value}
                    className={`type-option ${formData.propertyType === type.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="propertyType"
                      value={type.value}
                      checked={formData.propertyType === type.value}
                      onChange={handleChange}
                    />
                    <Icon size={24} />
                    <span>{t(type.labelKey)}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Location Section */}
          <div className="form-section">
            <h3>
              <MapPin size={20} />
              الموقع
            </h3>
            
            <div className="form-row two-cols">
              <div className="form-group">
                <label>المدينة <span className="required">*</span></label>
                <select 
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={loadingLocations}
                >
                  <option value="">{loadingLocations ? 'جاري التحميل...' : 'اختر المدينة'}</option>
                  {cities.map(city => (
                    <option key={city.label} value={city.label}>{city.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>المنطقة</label>
                <select 
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  disabled={!formData.city || loadingLocations}
                >
                  <option value="">اختر المنطقة</option>
                  {formData.city && (areasByCity[formData.city] || []).map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>
            </div>

            {formData.city && ((formData.region ? (districtsByArea[formData.region] || []) : (allDistrictsByCity[formData.city] || []))).length > 0 && (
              <div className="form-row">
                <div className="form-group full-width">
                  <label>الأحياء المفضلة <small>(يمكنك اختيار أكثر من حي)</small></label>
                  <div className="districts-multi-select">
                    {(formData.region ? (districtsByArea[formData.region] || []) : (allDistrictsByCity[formData.city] || [])).map(district => (
                      <label key={district} className="district-checkbox">
                        <input 
                          type="checkbox"
                          checked={formData.districts.includes(district)}
                          onChange={() => handleDistrictToggle(district)}
                        />
                        <span>{district}</span>
                      </label>
                    ))}
                  </div>
                  {formData.districts.length > 0 && (
                    <div className="selected-districts-summary">
                      تم اختيار {formData.districts.length} حي: {formData.districts.join('، ')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Budget Section */}
          <div className="form-section">
            <h3>
              <DollarSign size={20} />
              الميزانية
            </h3>
            
            <div className="form-row two-cols">
              <div className="form-group">
                <label>الميزانية (ر.س) <span className="required">*</span></label>
                <div className="input-with-icon">
                  <DollarSign size={18} />
                  <input 
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="الميزانية"
                    required
                  />
                </div>
              </div>
              
              {/* Payment Method for Buy */}
              {formData.requestType === 'buy' && (
                <div className="form-group">
                  <label>طريقة الدفع</label>
                  <select 
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                  >
                    <option value="cash">كاش</option>
                    <option value="bank">تمويل</option>
                  </select>
                </div>
              )}

              {/* Payment Plan for Rent */}
              {formData.requestType === 'rent' && (
                <div className="form-group">
                  <label>الدفعات</label>
                  <select 
                    name="paymentPlan"
                    value={formData.paymentPlan}
                    onChange={handleChange}
                  >
                    <option value="single">دفعة واحدة</option>
                    <option value="installments">دفعات</option>
                    <option value="monthly">شهري</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Additional Specifications */}
          <div className="form-section">
            <h3>
              <Layers size={20} />
              مواصفات إضافية
            </h3>
            
            <div className="form-row two-cols">
              <div className="form-group">
                <label>عمر العقار المطلوب (سنة)</label>
                <input 
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="عمر العقار"
                  min="0"
                />
              </div>

              {/* Floor for Rent only */}
              {formData.requestType === 'rent' && (
                <div className="form-group">
                  <label>الدور المفضل</label>
                  <select 
                    name="floor"
                    value={formData.floor}
                    onChange={handleChange}
                  >
                    <option value="any">أي دور</option>
                    <option value="ground">أرضي</option>
                    <option value="first">أول</option>
                    <option value="annex">ملحق</option>
                  </select>
                </div>
              )}
            </div>

          </div>

          {/* Requirements Section */}
          <div className="form-section">
            <h3>
              <ClipboardList size={20} />
              متطلبات إضافية
            </h3>
            
            <div className="form-group">
              <label>وصف متطلباتك</label>
              <textarea 
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                rows={4}
                placeholder="متطلبات إضافية"
              />
            </div>
          </div>

          {/* Contact Preference */}
          <div className="form-section">
            <h3>
              <Phone size={20} />
              طريقة التواصل المفضلة
            </h3>
            
            <div className="contact-options">
              <label className={`contact-option ${formData.preferredContact === 'whatsapp' ? 'selected' : ''}`}>
                <input 
                  type="radio"
                  name="preferredContact"
                  value="whatsapp"
                  checked={formData.preferredContact === 'whatsapp'}
                  onChange={handleChange}
                />
                <span>واتساب</span>
              </label>
              <label className={`contact-option ${formData.preferredContact === 'call' ? 'selected' : ''}`}>
                <input 
                  type="radio"
                  name="preferredContact"
                  value="call"
                  checked={formData.preferredContact === 'call'}
                  onChange={handleChange}
                />
                <span>اتصال هاتفي</span>
              </label>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={20} className="spinning" />
                جاري الإرسال...
              </>
            ) : (
              <>
                <Send size={20} />
                إرسال الطلب
              </>
            )}
          </button>
        </form>

        <div className="form-footer">
          <p>بتقديم هذا النموذج، أنت توافق على أن يتم التواصل معك من قبل مسوقين عقاريين</p>
        </div>
      </div>
    </div>
  );
};

export default PublicRequestForm;
